// Per-option photo rotation on the create-poll screen (fracto #533).
//
// The user rotates the preview in 90° steps; on submit we upload the ROTATED
// pixels, re-encoded from a canvas. A canvas export carries no EXIF, so the
// server's `exif_transpose` (backend/main.py, _compose_sprite, PR #139) sees an
// orientation-less file and cannot double-rotate. Unrotated options are sent as
// the original file, exactly as before this change.
//
// The preview the user rotates FROM is the EXIF-corrected image (browsers
// default `<img>` to `image-orientation: from-image`; we set it explicitly), so
// the source pixels drawn here must be EXIF-corrected too: `createImageBitmap`
// with `imageOrientation: "from-image"` where it exists, else an `<img>`
// element, which modern browsers also orient from EXIF when drawn to a canvas.

export const ROTATIONS = [0, 90, 180, 270];
export const JPEG_QUALITY = 0.9;
// Longest side of a rotated export. The server center-crops each option to a
// square and resamples it to 800 px (SPRITE_PANEL_PX), so 2048 keeps every
// pixel the sprite can show while bounding the re-encoded size: a source the
// picker accepted must not become an upload the API rejects merely by rotating.
export const MAX_EXPORT_SIDE = 2048;
// When an export is still over `maxBytes`, retry down this ladder (PNG sources
// drop to JPEG first, then smaller + more compressed) before giving up.
export const EXPORT_LADDER = [
  { jpegOnly: false, quality: JPEG_QUALITY, maxSide: MAX_EXPORT_SIDE },
  { jpegOnly: true, quality: JPEG_QUALITY, maxSide: MAX_EXPORT_SIDE },
  { jpegOnly: true, quality: 0.85, maxSide: 1600 },
  { jpegOnly: true, quality: 0.8, maxSide: 1200 },
  { jpegOnly: true, quality: 0.75, maxSide: 1000 },
];

/** The next 90° clockwise step: 0 → 90 → 180 → 270 → 0. */
export function nextRotation(rotation) {
  return (normalizeRotation(rotation) + 90) % 360;
}

/** Clamp any number onto the four supported steps (nearest multiple of 90, mod 360). */
export function normalizeRotation(rotation) {
  const n = Number(rotation);
  if (!Number.isFinite(n)) return 0;
  const step = Math.round(n / 90) * 90;
  return ((step % 360) + 360) % 360;
}

/**
 * Canvas size and draw transform for a source of `width` × `height` pixels
 * rotated `rotation` degrees clockwise, scaled down (never up) so the longest
 * side is at most `maxSide`. The canvas is sized to the rotated bounding box
 * (dimensions swap at 90/270); the draw translates to the canvas centre,
 * rotates, and blits the scaled source centred on the origin.
 */
export function rotatedCanvasGeometry({ width, height }, rotation, { maxSide = MAX_EXPORT_SIDE } = {}) {
  const deg = normalizeRotation(rotation);
  const scale = Math.min(1, maxSide / Math.max(width, height, 1));
  const drawWidth = Math.max(1, Math.round(width * scale));
  const drawHeight = Math.max(1, Math.round(height * scale));
  const swap = deg === 90 || deg === 270;
  const canvasWidth = swap ? drawHeight : drawWidth;
  const canvasHeight = swap ? drawWidth : drawHeight;
  return {
    width: canvasWidth,
    height: canvasHeight,
    rotation: deg,
    scale,
    angle: (deg * Math.PI) / 180,
    // translate(dx, dy) then rotate(angle) then drawImage(src, sx, sy, sw, sh)
    translate: [canvasWidth / 2, canvasHeight / 2],
    drawAt: [-drawWidth / 2, -drawHeight / 2],
    drawSize: [drawWidth, drawHeight],
  };
}

/** PNG stays PNG (lossless, alpha); everything else (JPEG, WebP) exports as JPEG. */
export function outputMimeType(sourceType) {
  return sourceType === 'image/png' ? 'image/png' : 'image/jpeg';
}

/** Keep the user's file name but make the extension match the exported type. */
export function rotatedFileName(name, mimeType) {
  const base = String(name || 'photo').replace(/\.[^./\\]+$/, '') || 'photo';
  return `${base}.${mimeType === 'image/png' ? 'png' : 'jpg'}`;
}

/** Draw `source` onto `ctx` rotated per `geometry` (from rotatedCanvasGeometry). */
export function drawRotated(ctx, source, geometry) {
  ctx.save();
  ctx.translate(geometry.translate[0], geometry.translate[1]);
  ctx.rotate(geometry.angle);
  ctx.drawImage(source, geometry.drawAt[0], geometry.drawAt[1], geometry.drawSize[0], geometry.drawSize[1]);
  ctx.restore();
}

/**
 * Decode `file` into something drawImage accepts, already EXIF-corrected.
 * Resolves `{ source, width, height, release }`.
 */
export async function loadOrientedSource(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release: () => { if (typeof bitmap.close === 'function') bitmap.close(); },
      };
    } catch (_) {
      // Older engines reject the option or the call; fall through to <img>.
    }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.style.imageOrientation = 'from-image';
    img.onload = () => {
      resolve({
        source: img,
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
        release: () => URL.revokeObjectURL(url),
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not decode the photo.'));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, mimeType, quality) {
  return new Promise((resolve, reject) => {
    if (typeof canvas.toBlob !== 'function') {
      reject(new Error('Canvas export is not supported in this browser.'));
      return;
    }
    const done = (blob) => (blob ? resolve(blob) : reject(new Error('Canvas export produced no image.')));
    if (mimeType === 'image/png') canvas.toBlob(done, mimeType);
    else canvas.toBlob(done, mimeType, quality);
  });
}

/** Thrown when no rung of the export ladder fits under `maxBytes`. */
export class RotatedImageTooLargeError extends Error {
  constructor(size, maxBytes) {
    super(`Rotated photo is ${size} bytes, over the ${maxBytes}-byte limit.`);
    this.name = 'RotatedImageTooLargeError';
    this.size = size;
    this.maxBytes = maxBytes;
  }
}

/**
 * Return a File holding `file`'s pixels rotated `rotation` degrees clockwise
 * and re-encoded without metadata, at most `maxBytes` long (default: no
 * limit). Rotation 0 returns `file` itself untouched.
 */
export async function rotateImageFile(file, rotation, { maxBytes = Infinity } = {}) {
  const deg = normalizeRotation(rotation);
  if (deg === 0) return file;
  const loaded = await loadOrientedSource(file);
  try {
    let lastSize = 0;
    for (const rung of EXPORT_LADDER) {
      const mimeType = rung.jpegOnly ? 'image/jpeg' : outputMimeType(file.type);
      // The PNG-keeping rung is the same as the JPEG one for non-PNG sources: skip the repeat.
      if (rung.jpegOnly && outputMimeType(file.type) === 'image/jpeg' && rung.maxSide === MAX_EXPORT_SIDE && rung.quality === JPEG_QUALITY) continue;
      const geometry = rotatedCanvasGeometry(loaded, deg, { maxSide: rung.maxSide });
      const canvas = document.createElement('canvas');
      canvas.width = geometry.width;
      canvas.height = geometry.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas is not supported in this browser.');
      drawRotated(ctx, loaded.source, geometry);
      const blob = await canvasToBlob(canvas, mimeType, rung.quality);
      lastSize = blob.size;
      if (blob.size <= maxBytes) {
        return new File([blob], rotatedFileName(file.name, mimeType), { type: mimeType, lastModified: Date.now() });
      }
    }
    throw new RotatedImageTooLargeError(lastSize, maxBytes);
  } finally {
    loaded.release();
  }
}
